import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Table, Button, Alert } from 'reactstrap';
import {
  getBlogs,
  createBlog,
  updateBlog,
  deleteBlogById,
  setCreateBlog,
  setUpdateBlog,
  setDeleteBlogById,
} from '../../redux/actions/blog';

import Loading from '../../components/Loading/Loading';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import BlogModal from './subcomponents/BlogModal';
import { hasPermission } from '../../utils/permissions';

function BlogList() {
  const dispatch = useDispatch();
  const listState = useSelector((state) => state.blog.get);
  const createState = useSelector((state) => state.blog.create);
  const updateState = useSelector((state) => state.blog.update);
  const deleteState = useSelector((state) => state.blog.deleteById);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    dispatch(getBlogs());
  }, [dispatch]);

  const handleCreate = useCallback(
    (formData) => {
      setLoading(true);
      dispatch(createBlog(formData));
    },
    [dispatch],
  );

  const handleEdit = useCallback(
    (id, formData) => {
      setLoading(true);
      dispatch(updateBlog(id, formData));
    },
    [dispatch],
  );

  const handleDelete = useCallback(
    (id) => {
      setLoading(true);
      dispatch(deleteBlogById(id));
    },
    [dispatch],
  );

  // Modal control methods
  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const openEditModal = (blog) => {
    setEditingBlog(blog);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditingBlog(null);
    setIsEditModalOpen(false);
  };

  const onBlogCreate = (formData) => {
    handleCreate(formData);
    closeCreateModal();
  };

  const onBlogEdit = (formData) => {
    if (editingBlog) {
      handleEdit(editingBlog.id, formData);
      closeEditModal();
    }
  };

  const handleAlert = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setShowAlert(true);
    const timer = setTimeout(() => setShowAlert(false), 3000);
    return () => clearTimeout(timer);
  };

  const clearReduxState = useCallback(
    (actionCreator, delay = 3500) => {
      setTimeout(() => {
        dispatch(actionCreator(null));
      }, delay);
    },
    [dispatch],
  );

  useEffect(() => {
    document.title = 'BGL - Blog';
    // Solo carga inicial
    setLoading(true);
    dispatch(getBlogs());
  }, [dispatch]);

  // Efecto separado para manejar respuestas de acciones
  useEffect(() => {
    if (listState) {
      setData(listState.data || []);
      setLoading(false);
    }
  }, [listState]);

  useEffect(() => {
    if (createState) {
      handleAlert(createState.status, createState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (createState.status === 'success') {
        dispatch(getBlogs());
      }
      clearReduxState(setCreateBlog);
    }
  }, [createState, clearReduxState, dispatch]);

  useEffect(() => {
    if (updateState) {
      handleAlert(updateState.status, updateState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (updateState.status === 'success') {
        dispatch(getBlogs());
      }
      clearReduxState(setUpdateBlog);
    }
  }, [updateState, clearReduxState, dispatch]);

  useEffect(() => {
    if (deleteState) {
      handleAlert(deleteState.status, deleteState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (deleteState.status === 'success') {
        dispatch(getBlogs());
      }
      clearReduxState(setDeleteBlogById);
    }
  }, [deleteState, clearReduxState, dispatch]);

  return loading ? (
    <Loading />
  ) : (
    <>
      <Container>
        <Alert
          color={alertType}
          isOpen={showAlert}
          toggle={() => setShowAlert(false)}
        >
          {alertMessage}
        </Alert>
      </Container>

      <Container className="container-border p-3">
        <article>
          {hasPermission('blogs.create') && (
            <Button
              className="float-right mb-2"
              size="sm"
              onClick={openCreateModal}
            >
              <i className="fa fa-plus mr-3"></i>
              <span>Crear post</span>
            </Button>
          )}
          <Button
            className="float-right mb-2"
            size="sm"
            onClick={() => refresh()}
          >
            <i className="fa fa-sync mr-3"></i>
            <span>Refrescar</span>
          </Button>
          <h2>Lista de Posts del Blog</h2>
        </article>

        <Table hover>
          <thead>
            <tr>
              <th>ID</th>
              <th>Imagen</th>
              <th>Título</th>
              <th>Autor</th>
              <th>Categoría</th>
              <th>Activo</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center">
                  No hay posts del blog disponibles
                </td>
              </tr>
            )}

            {data.map((row) => {
              return (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>
                    {row.imagen_destacada_url ? (
                      <img
                        src={row.imagen_destacada_url}
                        alt={row.titulo}
                        style={{ maxWidth: '100px', maxHeight: '60px' }}
                      />
                    ) : (
                      'Sin imagen'
                    )}
                  </td>
                  <td>{row.titulo}</td>
                  <td>{row.autor || 'N/A'}</td>
                  <td>{row.categoria || 'N/A'}</td>
                  <td>{row.active ? 'Sí' : 'No'}</td>
                  <td>
                    <>
                      {hasPermission('blogs.edit') && (
                        <Button
                          className="mr-2"
                          onClick={() => openEditModal(row)}
                        >
                          <i className="fa fa-edit"></i>
                        </Button>
                      )}
                      {hasPermission('blogs.delete') && (
                        <ConfirmationModal
                          btnClass="btn btn-danger"
                          iconClass="fa fa-trash"
                          title="Eliminar post del blog"
                          message="¿Estas seguro que quieres eliminar este post del blog? Esta operación no se podrá deshacer."
                          action={() => handleDelete(row.id)}
                          argument={row.id}
                          successMessage="Eliminar"
                          cancelMessage="Cancelar"
                          btnSuccessState="danger"
                          btnCancelState="secondary"
                        />
                      )}
                    </>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Container>

      {/* Create Blog Modal */}
      <BlogModal
        isOpen={isCreateModalOpen}
        onToggle={closeCreateModal}
        title="Crear post del blog"
        onSubmit={onBlogCreate}
      />

      {/* Edit Blog Modal */}
      <BlogModal
        isOpen={isEditModalOpen}
        onToggle={closeEditModal}
        title="Editar post del blog"
        defaultValue={editingBlog}
        onSubmit={onBlogEdit}
      />
    </>
  );
}

export default BlogList;
